/**
 * File open / save helpers that use the File System Access API where available
 * (Chrome, Edge, desktop PWAs — true "Save" back to the same file) and fall back to
 * classic <input type=file> / download links everywhere else (Safari, Firefox, iOS).
 */

export interface PickedFile {
  bytes: Uint8Array
  name: string
  handle: FileSystemFileHandle | null
}

type OpenPicker = (opts?: unknown) => Promise<FileSystemFileHandle[]>
type SavePicker = (opts?: unknown) => Promise<FileSystemFileHandle>

const w =
  typeof window !== 'undefined'
    ? (window as unknown as { showOpenFilePicker?: OpenPicker; showSaveFilePicker?: SavePicker })
    : {}

export const canSaveInPlace = typeof w.showSaveFilePicker === 'function'

const PDF_TYPES = [{ description: 'PDF documents', accept: { 'application/pdf': ['.pdf'] } }]

export async function readFile(file: File): Promise<Uint8Array> {
  return new Uint8Array(await file.arrayBuffer())
}

export async function pickPdfFiles(multiple = false): Promise<PickedFile[]> {
  if (w.showOpenFilePicker) {
    try {
      const handles = await w.showOpenFilePicker({ types: PDF_TYPES, multiple, id: 'folio-open' })
      return Promise.all(
        handles.map(async (h) => {
          const f = await h.getFile()
          return { bytes: await readFile(f), name: f.name, handle: h }
        }),
      )
    } catch (e) {
      if ((e as DOMException).name === 'AbortError') return []
      // fall through to the input element (e.g. cross-origin iframe restrictions)
    }
  }
  return pickWithInput('application/pdf,.pdf', multiple).then((files) =>
    Promise.all(files.map(async (f) => ({ bytes: await readFile(f), name: f.name, handle: null }))),
  )
}

export function pickWithInput(accept: string, multiple = false): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = accept
    input.multiple = multiple
    input.style.display = 'none'
    input.addEventListener('change', () => {
      resolve(input.files ? [...input.files] : [])
      input.remove()
    })
    input.addEventListener('cancel', () => {
      resolve([])
      input.remove()
    })
    document.body.appendChild(input)
    input.click()
  })
}

export async function verifyWritePermission(handle: FileSystemFileHandle) {
  const h = handle as FileSystemFileHandle & {
    queryPermission?: (d: { mode: string }) => Promise<PermissionState>
    requestPermission?: (d: { mode: string }) => Promise<PermissionState>
  }
  if (!h.queryPermission) return true
  if ((await h.queryPermission({ mode: 'readwrite' })) === 'granted') return true
  return (await h.requestPermission?.({ mode: 'readwrite' })) === 'granted'
}

export async function writeToHandle(handle: FileSystemFileHandle, bytes: Uint8Array) {
  const writable = await handle.createWritable()
  await writable.write(bytes as unknown as BufferSource)
  await writable.close()
}

/** Returns the handle written to, or null when the browser fell back to a download. */
export async function saveAs(
  bytes: Uint8Array,
  suggestedName: string,
): Promise<FileSystemFileHandle | null | 'cancelled'> {
  if (w.showSaveFilePicker) {
    try {
      const handle = await w.showSaveFilePicker({ suggestedName, types: PDF_TYPES, id: 'folio-save' })
      await writeToHandle(handle, bytes)
      return handle
    } catch (e) {
      if ((e as DOMException).name === 'AbortError') return 'cancelled'
    }
  }
  download(bytes, suggestedName)
  return null
}

export function download(bytes: Uint8Array, name: string, type = 'application/pdf') {
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type }))
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 30_000)
}

export const canShareFiles = () =>
  typeof navigator !== 'undefined' &&
  'canShare' in navigator &&
  navigator.canShare?.({ files: [new File([new Uint8Array(1)], 'x.pdf', { type: 'application/pdf' })] })

export async function shareFile(bytes: Uint8Array, name: string) {
  const file = new File([bytes as BlobPart], name, { type: 'application/pdf' })
  await navigator.share({ files: [file], title: name })
}

/** Print through a hidden iframe so we print the *edited* PDF, not the web page. */
export function printPdf(bytes: Uint8Array) {
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' }))
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  if (isIOS) {
    window.open(url, '_blank')
    return
  }
  const frame = document.createElement('iframe')
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0'
  frame.src = url
  frame.onload = () => {
    setTimeout(() => {
      try {
        frame.contentWindow?.focus()
        frame.contentWindow?.print()
      } catch {
        window.open(url, '_blank')
      }
    }, 100)
  }
  document.body.appendChild(frame)
  setTimeout(() => {
    frame.remove()
    URL.revokeObjectURL(url)
  }, 60_000 * 5)
}

export function ensurePdfName(name: string) {
  const base = name.trim() || 'document'
  return /\.pdf$/i.test(base) ? base : `${base}.pdf`
}

export function withSuffix(name: string, suffix: string) {
  return ensurePdfName(name).replace(/\.pdf$/i, `${suffix}.pdf`)
}

export function formatBytes(n: number) {
  if (n < 1024) return `${n} B`
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / 1024 ** 2).toFixed(1)} MB`
}
